import React, { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import styles from './AddPatientModal.module.css';
import {
  FaUser,
  FaTimes,
  FaSave,
  FaCalendarAlt,
  FaHome,
  FaPlus,
  FaTrash,
  FaPills,
  FaClipboardList,
  FaStickyNote,
  FaLink
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { useUserProfile } from '../../contexts/UserProfileContext';
import {
  claimPatient,
  findProjectPatientsByStudyId,
} from '../../utils/patientAccess';

// Patient status options
const PATIENT_STATUSES = [
  { value: 'planned', label: 'Planlanan', description: 'Çalışmaya dahil edilmesi planlanan' },
  { value: 'screened', label: 'Screen Edilen', description: 'Screening işlemi yapılan' },
  { value: 'screenFail', label: 'Screen Fail', description: 'Screening başarısız olan' },
  { value: 'enrolled', label: 'Dahil Edilen', description: 'Çalışmaya dahil edilen' },
  { value: 'active', label: 'Aktif', description: 'Aktif olarak çalışmada olan' },
  { value: 'onMedication', label: 'İlaç Alan', description: 'İlaç tedavisi alan' },
  { value: 'completed', label: 'Tamamlanan', description: 'Çalışmayı tamamlayan' },
  { value: 'withdrawn', label: 'Çekilen', description: 'Çalışmadan çekilen' }
];

const AddPatientModal = ({ project, patient, onClose, onSave }) => {
  const { isDarkMode } = useTheme();
  const { employeeId } = useUserProfile();
  const isEditing = !!patient;

  const [formData, setFormData] = useState({
    patientId: '',
    status: 'planned',
    enrollmentDate: '',
    firstDoseDate: '',
    currentVisitNumber: '',
    hasHomeVisit: false,
    homeVisitDates: [],
    homeVisitInfo: '',
    notes: ''
  });

  const [newHomeVisitDate, setNewHomeVisitDate] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  // Patient that already exists in this project under the entered Hasta ID.
  // Set while we ask the user whether they want to use it instead.
  const [existingPatient, setExistingPatient] = useState(null);
  const [linking, setLinking] = useState(false);

  // Populate form data when editing
  useEffect(() => {
    if (patient) {
      setFormData({
        patientId: patient.patientId || '',
        status: patient.status || 'planned',
        enrollmentDate: patient.enrollmentDate || '',
        firstDoseDate: patient.firstDoseDate || '',
        currentVisitNumber: patient.currentVisitNumber || '',
        hasHomeVisit: patient.hasHomeVisit || false,
        homeVisitDates: patient.homeVisitDates || [],
        homeVisitInfo: patient.homeVisitInfo || '',
        notes: patient.notes || ''
      });
    }
  }, [patient]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAddHomeVisitDate = () => {
    if (!newHomeVisitDate) {
      toast.warning('Lütfen bir tarih seçin');
      return;
    }

    if (formData.homeVisitDates.includes(newHomeVisitDate)) {
      toast.warning('Bu tarih zaten eklenmiş');
      return;
    }

    setFormData(prev => ({
      ...prev,
      homeVisitDates: [...prev.homeVisitDates, newHomeVisitDate].sort()
    }));
    setNewHomeVisitDate('');
  };

  const handleRemoveHomeVisitDate = (dateToRemove) => {
    setFormData(prev => ({
      ...prev,
      homeVisitDates: prev.homeVisitDates.filter(date => date !== dateToRemove)
    }));
  };

  // Patients already registered in this project under the entered Hasta ID.
  // When editing, the patient being edited is not a conflict with itself.
  const findExistingPatients = async (patientId) => {
    try {
      const matches = await findProjectPatientsByStudyId(project.id, patientId);
      return isEditing ? matches.filter(match => match.id !== patient.id) : matches;
    } catch (error) {
      console.error('Error checking duplicate patient ID:', error);
      return [];
    }
  };

  // "Use the existing patient": the record itself is left untouched, the user is
  // only added to its owners so it shows up in their own patient list.
  const handleUseExistingPatient = async () => {
    if (!existingPatient) return;

    setLinking(true);
    try {
      await claimPatient(existingPatient.id, employeeId);

      const linkedPatient = {
        ...existingPatient,
        ownerIds: Array.from(
          new Set([...(existingPatient.ownerIds || []), employeeId].filter(Boolean))
        )
      };

      toast.success('Mevcut hasta listenize eklendi');
      setExistingPatient(null);
      onSave(linkedPatient);
    } catch (error) {
      console.error('Error linking existing patient:', error);
      toast.error('Hasta listenize eklenirken hata oluştu');
    } finally {
      setLinking(false);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.patientId.trim()) {
      newErrors.patientId = 'Hasta ID zorunludur';
    }

    if (!formData.status) {
      newErrors.status = 'Durum seçimi zorunludur';
    }

    // If status is enrolled, active, onMedication - enrollment date should be set
    if (['enrolled', 'active', 'onMedication'].includes(formData.status) && !formData.enrollmentDate) {
      newErrors.enrollmentDate = 'Bu durum için kayıt tarihi gereklidir';
    }

    // If on medication, first dose date should ideally be set
    if (formData.status === 'onMedication' && !formData.firstDoseDate) {
      newErrors.firstDoseDate = 'İlaç alan hasta için ilk doz tarihi önerilir';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Lütfen zorunlu alanları doldurun');
      return;
    }

    setSaving(true);

    try {
      const matches = await findExistingPatients(formData.patientId);

      if (matches.length > 0) {
        if (isEditing) {
          // Renaming onto another patient's id is a real conflict.
          setErrors(prev => ({ ...prev, patientId: 'Bu Hasta ID zaten kullanımda' }));
          toast.error('Bu Hasta ID bu projede zaten mevcut!');
          setSaving(false);
          return;
        }

        // Somebody already registered this patient in this project. Offer to
        // use that record instead of creating a second one for the same person.
        setExistingPatient(matches[0]);
        setSaving(false);
        return;
      }

      const patientData = {
        projectId: project.id,
        patientId: formData.patientId.trim(),
        status: formData.status,
        enrollmentDate: formData.enrollmentDate || null,
        firstDoseDate: formData.firstDoseDate || null,
        currentVisitNumber: formData.currentVisitNumber ? parseInt(formData.currentVisitNumber) : null,
        hasHomeVisit: formData.hasHomeVisit,
        homeVisitDates: formData.homeVisitDates,
        homeVisitInfo: formData.homeVisitInfo.trim(),
        notes: formData.notes.trim(),
        updatedAt: serverTimestamp()
      };

      let savedPatient;

      if (isEditing) {
        // Update existing patient (ownerIds is left untouched, so every owner
        // keeps the patient)
        const patientRef = doc(db, 'Patients', patient.id);
        await updateDoc(patientRef, patientData);
        savedPatient = { ...patient, ...patientData };
        toast.success('Hasta bilgileri başarıyla güncellendi');
      } else {
        // Add new patient. ownerIds makes the patient visible to its creator
        // even before the first visit is booked with them.
        patientData.createdAt = serverTimestamp();
        patientData.createdBy = employeeId || null;
        patientData.ownerIds = employeeId ? [employeeId] : [];
        const patientRef = await addDoc(collection(db, 'Patients'), patientData);
        savedPatient = { id: patientRef.id, ...patientData };
        toast.success('Hasta başarıyla eklendi');
      }

      onSave(savedPatient);
    } catch (error) {
      console.error('Error saving patient:', error);
      toast.error(isEditing ? 'Hasta güncellenirken hata oluştu' : 'Hasta eklenirken hata oluştu');
    } finally {
      setSaving(false);
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('tr-TR');
    } catch {
      return dateString;
    }
  };

  return (
    <div className={`${styles.modalBackdrop} ${isDarkMode ? styles.darkMode : ''}`}>
      <div className={`${styles.modal} ${isDarkMode ? styles.darkMode : ''}`}>
        {/* Header */}
        <div className={styles.modalHeader}>
          <div className={styles.headerTitle}>
            <FaUser className={styles.headerIcon} />
            <h2>{isEditing ? 'Hasta Bilgilerini Düzenle' : 'Yeni Hasta Ekle'}</h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* The entered Hasta ID already exists in this project: offer the
            existing record instead of creating a duplicate for the same person */}
        {existingPatient && (
          <div className={styles.confirmOverlay}>
            <div className={styles.confirmBox}>
              <div className={styles.confirmIcon}>
                <FaLink />
              </div>
              <h3 className={styles.confirmTitle}>Bu hasta zaten kayıtlı</h3>
              <p className={styles.confirmText}>
                <strong>{existingPatient.patientId}</strong> bu projede zaten
                kayıtlı. Yeni bir kayıt oluşturmak yerine mevcut hastayı
                kullanmak ister misiniz?
              </p>
              <p className={styles.confirmNote}>
                Mevcut hasta bilgileri değiştirilmez, hasta yalnızca sizin
                listenize eklenir.
              </p>
              <div className={styles.confirmActions}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setExistingPatient(null)}
                  disabled={linking}
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  className={styles.saveButton}
                  onClick={handleUseExistingPatient}
                  disabled={linking}
                >
                  {linking ? (
                    <>
                      <span className={styles.spinner}></span>
                      Ekleniyor...
                    </>
                  ) : (
                    <>
                      <FaLink /> Mevcut Hastayı Kullan
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.formContent}>
            {/* Basic Information Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>
                <FaClipboardList className={styles.sectionIcon} />
                Temel Bilgiler
              </h3>

              <div className={styles.formGrid}>
                {/* Patient ID */}
                <div className={styles.formGroup}>
                  <label htmlFor="patientId" className={styles.label}>
                    Hasta ID <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    id="patientId"
                    name="patientId"
                    value={formData.patientId}
                    onChange={handleChange}
                    placeholder="Örn: PAT-001"
                    className={`${styles.input} ${errors.patientId ? styles.inputError : ''}`}
                  />
                  {errors.patientId && (
                    <span className={styles.errorText}>{errors.patientId}</span>
                  )}
                </div>

                {/* Status */}
                <div className={styles.formGroup}>
                  <label htmlFor="status" className={styles.label}>
                    Durum <span className={styles.required}>*</span>
                  </label>
                  <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className={`${styles.select} ${errors.status ? styles.inputError : ''}`}
                  >
                    {PATIENT_STATUSES.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  {errors.status && (
                    <span className={styles.errorText}>{errors.status}</span>
                  )}
                  <span className={styles.helperText}>
                    {PATIENT_STATUSES.find(s => s.value === formData.status)?.description}
                  </span>
                </div>
              </div>
            </div>

            {/* Dates Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>
                <FaCalendarAlt className={styles.sectionIcon} />
                Tarih Bilgileri
              </h3>

              <div className={styles.formGrid}>
                {/* Enrollment Date */}
                <div className={styles.formGroup}>
                  <label htmlFor="enrollmentDate" className={styles.label}>
                    Kayıt Tarihi (Enrollment Date)
                    {['enrolled', 'active', 'onMedication'].includes(formData.status) && (
                      <span className={styles.required}>*</span>
                    )}
                  </label>
                  <input
                    type="date"
                    id="enrollmentDate"
                    name="enrollmentDate"
                    value={formData.enrollmentDate}
                    onChange={handleChange}
                    className={`${styles.input} ${errors.enrollmentDate ? styles.inputError : ''}`}
                  />
                  {errors.enrollmentDate && (
                    <span className={styles.errorText}>{errors.enrollmentDate}</span>
                  )}
                </div>

                {/* First Dose Date */}
                <div className={styles.formGroup}>
                  <label htmlFor="firstDoseDate" className={styles.label}>
                    İlk Doz Tarihi (POF)
                    {formData.status === 'onMedication' && (
                      <span className={styles.recommended}>önerilen</span>
                    )}
                  </label>
                  <input
                    type="date"
                    id="firstDoseDate"
                    name="firstDoseDate"
                    value={formData.firstDoseDate}
                    onChange={handleChange}
                    className={`${styles.input} ${errors.firstDoseDate ? styles.inputError : ''}`}
                  />
                  {errors.firstDoseDate && (
                    <span className={styles.errorText}>{errors.firstDoseDate}</span>
                  )}
                </div>

                {/* Current Visit Number */}
                <div className={styles.formGroup}>
                  <label htmlFor="currentVisitNumber" className={styles.label}>
                    Mevcut Visit Numarası
                  </label>
                  <input
                    type="number"
                    id="currentVisitNumber"
                    name="currentVisitNumber"
                    value={formData.currentVisitNumber}
                    onChange={handleChange}
                    min="1"
                    placeholder="Örn: 3"
                    className={styles.input}
                  />
                </div>
              </div>
            </div>

            {/* Home Visit Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>
                <FaHome className={styles.sectionIcon} />
                Home Visit Bilgileri
              </h3>

              {/* Home Visit Toggle */}
              <div className={styles.toggleGroup}>
                <label className={styles.toggleLabel}>
                  <input
                    type="checkbox"
                    name="hasHomeVisit"
                    checked={formData.hasHomeVisit}
                    onChange={handleChange}
                    className={styles.toggleCheckbox}
                  />
                  <span className={styles.toggleSlider}></span>
                  <span className={styles.toggleText}>Home Visit Yapıldı</span>
                </label>
              </div>

              {/* Home Visit Details (shown when hasHomeVisit is true) */}
              {formData.hasHomeVisit && (
                <div className={styles.homeVisitDetails}>
                  {/* Add Home Visit Date */}
                  <div className={styles.addDateRow}>
                    <div className={styles.dateInputGroup}>
                      <input
                        type="date"
                        value={newHomeVisitDate}
                        onChange={(e) => setNewHomeVisitDate(e.target.value)}
                        className={styles.dateInput}
                      />
                      <button
                        type="button"
                        onClick={handleAddHomeVisitDate}
                        className={styles.addDateBtn}
                      >
                        <FaPlus /> Tarih Ekle
                      </button>
                    </div>
                  </div>

                  {/* Home Visit Dates List */}
                  {formData.homeVisitDates.length > 0 && (
                    <div className={styles.datesList}>
                      <label className={styles.datesLabel}>Eklenen Tarihler:</label>
                      <div className={styles.datesChips}>
                        {formData.homeVisitDates.map((date, idx) => (
                          <div key={idx} className={styles.dateChip}>
                            <span>{formatDateForDisplay(date)}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveHomeVisitDate(date)}
                              className={styles.removeDateBtn}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Home Visit Info */}
                  <div className={styles.formGroup}>
                    <label htmlFor="homeVisitInfo" className={styles.label}>
                      Home Visit Notları
                    </label>
                    <textarea
                      id="homeVisitInfo"
                      name="homeVisitInfo"
                      value={formData.homeVisitInfo}
                      onChange={handleChange}
                      rows="3"
                      placeholder="Home visit ile ilgili ek bilgiler..."
                      className={styles.textarea}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Notes Section */}
            <div className={styles.formSection}>
              <h3 className={styles.sectionTitle}>
                <FaStickyNote className={styles.sectionIcon} />
                Ek Notlar
              </h3>

              <div className={styles.formGroup}>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="4"
                  placeholder="Hasta ile ilgili ek notlar, önemli bilgiler..."
                  className={styles.textarea}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className={styles.modalFooter}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={saving}
            >
              İptal
            </button>
            <button
              type="submit"
              className={styles.saveButton}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className={styles.spinner}></span>
                  Kaydediliyor...
                </>
              ) : (
                <>
                  <FaSave /> {isEditing ? 'Güncelle' : 'Kaydet'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddPatientModal;
